// PR-P3 — the one place a free amount is entered: an ad-hoc credit/debit line
// on a statement. Reuses POST /statement-entries (manual ledger entry). Never
// overwrites a computed number; always a separate line.
import { useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient, extractErrorMessage } from '@/lib/api';

export function AddOtherLine({
  subjectKind, subjectId, onAdded,
}: {
  subjectKind: 'vehicle' | 'driver' | 'investor'; subjectId: string; onAdded: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [kind, setKind] = useState<'credit' | 'debit'>('debit');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  async function add() {
    if (label.trim().length < 3 || !(Number(amount) > 0)) { window.alert('Enter a label (3+ chars) and an amount.'); return; }
    setBusy(true);
    try {
      await apiClient.post('/api/v1/statement-entries', {
        subjectKind, subjectId, postedOn: date,
        creditSar: kind === 'credit' ? Number(amount) : 0,
        debitSar: kind === 'debit' ? Number(amount) : 0,
        description: label.trim(),
      });
      setLabel(''); setAmount(''); setOpen(false); onAdded();
    } catch (e) { window.alert(extractErrorMessage(e)); } finally { setBusy(false); }
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
        <Plus className="h-3.5 w-3.5" /> Add line
      </button>
    );
  }
  return (
    <div className="rounded-md border border-border p-2 space-y-2 bg-muted/20" data-add-line>
      <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. Tip, Fine, Bonus)" className="h-9 text-sm" />
      <div className="flex gap-2">
        <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" className="h-9 text-sm" />
        <select value={kind} onChange={(e) => setKind(e.target.value as 'credit' | 'debit')} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
          <option value="debit">Debit (−)</option>
          <option value="credit">Credit (+)</option>
        </select>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 text-sm w-36" />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
        <Button size="sm" onClick={add} disabled={busy} className="gap-1">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add</Button>
      </div>
    </div>
  );
}
